/**
 * Formatting, matching the prototype exactly.
 *
 * Money is one formatter, `formatMoney`, and every screen uses it (brief 08, section 2):
 * `$1 248` and `14 500 000 so'm`, thousands grouped by a narrow no-break space, figures
 * tabular in tables. Dates are `dd.MM` or `dd.MM.yyyy`, the way the prototype's tables
 * show them.
 *
 * Money arrives from the API as decimal strings and is formatted, never converted: a
 * float would round a figure before anybody saw it. A number is accepted for the
 * convenience of computed values and goes through the same string path.
 */
import type { Currency } from "@/api/types";
import { activeLocale } from "@/i18n";

/** Thousands separator for every figure the product shows: U+202F, narrow no-break space. */
export const GROUP_SPACE = " ";

/** Groups an unsigned digit string in threes: "14500000" -> "14 500 000". */
export function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP_SPACE);
}

interface DecimalParts {
  readonly negative: boolean;
  readonly whole: string;
  readonly fraction: string;
}

/** Splits a decimal string into its parts without any floating-point arithmetic. */
export function splitDecimal(amount: number | string): DecimalParts {
  const text = typeof amount === "number" ? numberToDecimal(amount) : amount.trim();
  const negative = text.startsWith("-");
  const unsigned = negative ? text.slice(1) : text;
  const [whole = "", fraction = ""] = unsigned.split(".");
  if (!/^\d+$/.test(whole) || !/^\d*$/.test(fraction)) {
    throw new Error(`not a decimal money amount: "${text}"`);
  }
  return { negative, whole: whole.replace(/^0+(?=\d)/, ""), fraction };
}

function numberToDecimal(value: number): string {
  if (!Number.isFinite(value)) {
    throw new Error(`not a finite money amount: ${String(value)}`);
  }
  return value.toFixed(2);
}

/**
 * The one money formatter.
 *
 * - UZS: whole som with the `so'm` suffix. Tiyin are never shown in this product, so the
 *   fraction is dropped rather than rounded into a figure nobody paid.
 * - USD: `$` before the figure; cents only when they are not zero.
 *
 * A negative figure takes the typographic minus, and zero never carries a sign.
 */
export function formatMoney(amount: number | string, currency: Currency): string {
  const parts = splitDecimal(amount);
  const isZero = parts.whole === "0" && /^0*$/.test(parts.fraction);
  const sign = parts.negative && !isZero ? "−" : "";
  if (currency === "USD") {
    const cents = parts.fraction.slice(0, 2).padEnd(2, "0");
    const tail = cents === "00" ? "" : `.${cents}`;
    return moneyDisplay === "code"
      ? `${sign}${groupDigits(parts.whole)}${tail}${GROUP_SPACE}USD`
      : `${sign}$${groupDigits(parts.whole)}${tail}`;
  }
  return `${sign}${groupDigits(parts.whole)}${GROUP_SPACE}${moneyDisplay === "code" ? "UZS" : "so'm"}`;
}

/** The person's own choice in their profile (D9.110): how an amount is written, never its value. */
let moneyDisplay: "symbol" | "code" = "symbol";

export function setMoneyDisplay(next: "symbol" | "code"): void {
  moneyDisplay = next;
}

/** Som, with the suffix. Same formatter as `formatMoney(amount, "UZS")`. */
export function formatUzs(amount: number | string): string {
  return formatMoney(amount, "UZS");
}

/** Dollars, with the `$` prefix. Same formatter as `formatMoney(amount, "USD")`. */
export function formatUsd(amount: number | string): string {
  return formatMoney(amount, "USD");
}

/** A signed amount for the "+8 400 000 so'm bugun" movement line. */
export function formatMovement(value: number, currency: Currency): string {
  if (value === 0) {
    return "";
  }
  const sign = value > 0 ? "+" : "";
  return `${sign}${formatMoney(value, currency)}`;
}

/** The exchange-rate figure, without trailing zeros: 12 650 rather than 12650.0000. */
export function formatRate(rate: number): string {
  return groupDigits(Math.round(rate).toString());
}

function parts(iso: string): { day: string; month: string; year: string } | null {
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return {
    day: String(parsed.getDate()).padStart(2, "0"),
    month: String(parsed.getMonth() + 1).padStart(2, "0"),
    year: String(parsed.getFullYear()),
  };
}

/** `dd.MM` — the compact form used in table columns. */
export function formatDayMonth(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const value = parts(iso);
  return value === null ? "—" : `${value.day}.${value.month}`;
}

/** `dd.MM.yyyy` — the long form used in forms and cards. */
export function formatDate(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const value = parts(iso);
  return value === null ? "—" : `${value.day}.${value.month}.${value.year}`;
}

const MONTHS_UZ = ["yan", "fev", "mart", "apr", "may", "iyun", "iyul", "avg", "sen", "okt", "noy", "dek"] as const;
const MONTHS_RU = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"] as const;

/**
 * `5 noy`: the date in rows, chips and tags (the K3 prototype). The day is the local one, like
 * every date here, and carries no leading zero. Russian follows the person's own language.
 */
export function formatShortDate(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  const months = activeLocale() === "ru" ? MONTHS_RU : MONTHS_UZ;
  return `${String(parsed.getDate())} ${months[parsed.getMonth()] ?? ""}`;
}

/** The twelve short month names, in the language the person reads. */
export function shortMonths(): readonly string[] {
  return activeLocale() === "ru" ? MONTHS_RU : MONTHS_UZ;
}

/** `okt 2025`: a trip's month, in the language the person reads. */
export function formatMonthYear(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  const months = activeLocale() === "ru" ? MONTHS_RU : MONTHS_UZ;
  return `${months[parsed.getMonth()] ?? ""} ${String(parsed.getFullYear())}`;
}

/** Today in the `yyyy-MM-dd` form the API expects. */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/** The local calendar day of an instant, as `yyyy-MM-dd`: an event at 00:30 is the day it happened. */
export function localDayIso(iso: string): string {
  const parsed = new Date(iso);
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${String(parsed.getFullYear())}-${month}-${day}`;
}

/** `HH:mm` in the browser's own clock: the time a feed row or a task shows. */
export function formatTimeOfDay(iso: string): string {
  const parsed = new Date(iso);
  const hours = String(parsed.getHours()).padStart(2, "0");
  const minutes = String(parsed.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}

/** "bugun" for today, otherwise `dd.MM` — matches the prototype's MUDDAT column. */
export function formatDueLabel(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  return iso === todayIso() ? "bugun" : formatDayMonth(iso);
}

/** Initials for an avatar chip: "Aziz Karimov" → "AK". */
export function initials(fullName: string): string {
  return fullName
    .split(/\s+/)
    .filter((part) => part.length > 0)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");
}

/**
 * The two-letter tile beside the agency name in the sidebar.
 *
 * Usually the initials of the first two words: "Silk Route" gives "SR". When the first
 * word is itself an acronym it is already the mark people recognise, so it wins on its
 * own: "DD Travel" gives "DD", not "DT".
 */
export function tenantMark(name: string): string {
  const words = name.split(/\s+/).filter((part) => part.length > 0);
  const first = words[0];
  if (first !== undefined && first.length >= 2 && first === first.toUpperCase()) {
    return first.slice(0, 2);
  }
  return initials(name);
}

/**
 * A phone number as it is read aloud: `+998 90 111 01 01`. Numbers are stored in one shape
 * (Q78, `+998901110101`) while older rows were typed with spaces, so the clients table showed
 * both side by side. An Uzbek number of the stored shape is grouped; anything else (a foreign
 * number, a partial one) is shown exactly as it was stored rather than guessed at. An empty
 * value is a dash.
 */
export function formatPhone(phone: string | null | undefined): string {
  const value = (phone ?? "").trim();
  if (value === "") {
    return "—";
  }
  const digits = value.replace(/[\s\-()]/g, "");
  const uz = /^\+998(\d{2})(\d{3})(\d{2})(\d{2})$/.exec(digits);
  if (uz === null) {
    return value;
  }
  const [, code, first, second, third] = uz;
  return `+998 ${code ?? ""} ${first ?? ""} ${second ?? ""} ${third ?? ""}`;
}
